import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const IMAGES = {
  primary: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/q4vv9lch_IMG_1564.jpeg",
  family1: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/0h2dxmqq_IMG_9542small.jpg",
  family2: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/t8b4fema_IMG_5449-preview1.jpg",
  family3: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/he51h3yr_IMG_1782.JPG",
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const values = [
  {
    icon: Target,
    title: 'Precision',
    description: 'Every detail matters. We measure twice and cut once, ensuring perfection in every project.',
  },
  {
    icon: Heart,
    title: 'Integrity',
    description: 'Honest communication, fair pricing, and transparent processes from start to finish.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We hold ourselves to the highest standards of craftsmanship and professionalism.',
  },
  {
    icon: Users,
    title: 'Partnership',
    description: 'We work alongside you, treating your project as if it were our own.',
  },
];

const process = [
  {
    step: '01',
    title: 'Consultation',
    description: 'We meet to discuss your vision, requirements, and budget.',
  },
  {
    step: '02',
    title: 'Design, Planning & Permitting',
    description: 'Our team creates detailed plans and provides a comprehensive quote.',
  },
  {
    step: '03',
    title: 'Construction',
    description: 'Expert craftsmen bring your project to life with precision and quality.',
  },
  {
    step: '04',
    title: 'Completion',
    description: 'Final walkthrough and turnover of your finished space.',
  },
];

export const About = () => {
  return (
    <div className="min-h-screen pt-48" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 bg-ktm-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="space-y-6"
              initial="initial"
              animate="animate"
              variants={fadeInUp}
            >
              <span className="inline-block bg-ktm-red text-white px-4 py-2 text-sm font-bold uppercase tracking-wider">
                About Us
              </span>
              <h1 className="font-chivo font-black text-4xl md:text-5xl text-ktm-navy leading-tight">
                Building California &amp;<br />
                Nevada&apos;s Future<br />
                <span className="text-ktm-red">Since Day One</span>
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed">
                KTM Construction is a locally-owned general contracting company serving 
                California and Nevada. The name KTM represents what matters most to founder Matt: 
                <strong>K</strong>ylee (his daughter), <strong>T</strong>racie (his wife), and <strong>M</strong>att himself — 
                a true family business built on love and dedication.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                We specialize in both multifamily and commercial construction, bringing years of expertise 
                and a commitment to excellence to every project we undertake. Our team of skilled 
                professionals takes pride in transforming spaces and exceeding client expectations.
              </p>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="aspect-[4/5] overflow-hidden border-4 border-ktm-navy shadow-2xl">
                <img 
                  src={IMAGES.primary} 
                  alt="The KTM family" 
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative red frame */}
              <div className="absolute -z-10 top-6 -right-6 w-full h-full border-4 border-ktm-red/40" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Family Story */}
      <section className="py-20 bg-ktm-muted" data-testid="family-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">
              The Heart of KTM
            </span>
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy mt-4">
              A Family Business
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mt-4 max-w-2xl mx-auto">
              Every project carries the same care we put into our own home. 
              These are the people behind the work — the family that makes KTM what it is.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              className="aspect-[4/5] overflow-hidden border-4 border-ktm-navy"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.0 }}
            >
              <img 
                src={IMAGES.family1} 
                alt="KTM family moment" 
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              className="aspect-[4/5] overflow-hidden border-4 border-ktm-red md:mt-8"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <img 
                src={IMAGES.family2} 
                alt="KTM family moment" 
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              className="aspect-[4/5] overflow-hidden border-4 border-ktm-navy"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src={IMAGES.family3} 
                alt="KTM family roots" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-ktm-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              className="p-8 border border-white/20"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-ktm-red flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-chivo font-bold text-2xl text-white mb-4">Our Mission</h3>
              <p className="text-slate-300 leading-relaxed">
                To deliver exceptional construction services that exceed expectations, 
                building lasting relationships with our clients through integrity, 
                quality craftsmanship, and unwavering commitment to their vision.
              </p>
            </motion.div>

            <motion.div
              className="p-8 border border-white/20"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-ktm-red flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-chivo font-bold text-2xl text-white mb-4">Our Vision</h3>
              <p className="text-slate-300 leading-relaxed">
                To be California and Nevada&apos;s most trusted construction partner, known for 
                transforming spaces and building dreams with precision, innovation, 
                and an uncompromising dedication to excellence.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-ktm-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">
              What Drives Us
            </span>
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy mt-4">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-ktm-navy mx-auto flex items-center justify-center mb-6">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-chivo font-bold text-lg text-ktm-navy mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">
              How We Work
            </span>
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy mt-4">
              Our Process
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-ktm-red flex items-center justify-center">
                  <span className="font-chivo font-bold text-white text-sm">
                    {step.step}
                  </span>
                </div>
                <div className="bg-ktm-muted p-8 pt-12">
                  <h3 className="font-chivo font-bold text-lg text-ktm-navy mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-ktm-red">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-white mb-6">
              Let&apos;s Build Together
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Ready to start your next construction project? 
              Contact us today to discuss your vision.
            </p>
            <Link to="/contact?quote=true">
              <Button 
                className="bg-white text-ktm-navy h-14 px-8 rounded-none font-bold uppercase tracking-wider hover:bg-slate-100"
                data-testid="about-cta-btn"
              >
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
